import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import {
	Box,
	CircularProgress,
	Modal,
	Typography,
	useMediaQuery,
	Grid2 as Grid
} from '@mui/material';
import { ReactComponent as CrossIcon } from '../../Assets/Images/close-circle-grey.svg';
import DownloadIcon from '../../Assets/Images/svgs/download.svg';
import './Dashboard/pdf-pop-up-view.css';
import GeneralModal from './Modal/GeneralModal/general-modal.js';

const CustomText = ({ title = '', value = '' }) => (
	<Box
		display='flex'
		alignItems='start'
		width='300px'
		justifyContent='space-between'
	>
		<Typography
			mt='10px'
			color='#8E8E8E'
			className={'medium11'}
			fontSize='15px'
			width='50%'
			sx={{ wordWrap: 'break-word' }}
		>
			{title}:
		</Typography>
		<Typography
			mt='10px'
			color='#D9D9D9'
			className={'medium11'}
			fontSize='15px'
			width='50%'
			sx={{ wordWrap: 'break-word' }}
		>
			{value}
		</Typography>
	</Box>
);

const ViewDocumentPopUp = ({
	open = false,
	docName = '',
	DocUrl = '',
	isPdf = false,
	handleClose = () => {},
	data = {},
	inventoryPage = false,
	handleUpdateClick,
	handleCloseConfirmation,
	handleOpenConfirmation,
	openConfirmation
}) => {
	const pdfContainerRef = useRef();
	const divRef = useRef();
	const isMobile = useMediaQuery('(max-width: 900px)');
	const [documentLoading, setDocumentLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [numPages, setNumPages] = useState(null);
	const noDownloadIcon = ['Reserved', 'Available'];

	const handleScroll = () => {
		if (pdfContainerRef.current) {
			const scrollTop = pdfContainerRef.current.scrollTop;
			const pageHeight = pdfContainerRef.current.scrollHeight / numPages;
			const newPage = Math.floor(scrollTop / pageHeight) + 1;

			setCurrentPage(newPage);
		}
	};

	const onDocumentLoadSuccess = ({ numPages }) => {
		setNumPages(numPages);
		setCurrentPage(1);
	};

	const onDocumentLoadError = () => {
		setCurrentPage(1);
		setDocumentLoading(false);
	};

	useEffect(() => {
		if (!isPdf) {
			setDocumentLoading(false);
		}
	}, [isPdf]);

	return (
		<Modal
			open={open}
			onClose={() => {
				setDocumentLoading(true);
				handleClose();
			}}
			className='popUp-container'
		>
			<Box
				ref={divRef}
				className='popUp-content'
				display='flex'
				sx={{
					width:
						(DocUrl
							&& (isMobile ? '80%' : (isPdf && 'fit-content') || '600px'))
						|| 'fit-content',
					height: (isMobile && ((DocUrl && 'fit-content') || '94vh')) || 'auto',
					maxHeight: '90vh',
					overflowY: 'auto',
					scrollbarWidth: '0px',
					color: 'grey',
					padding: (isMobile && '6px 24px 24px 24px') || '24px'
				}}
			>
				<GeneralModal
					open={openConfirmation}
					onClose={handleCloseConfirmation}
					btn2OnClick={() => {
						handleCloseConfirmation();
						handleUpdateClick('used');
					}}
					genText={'Important Note!'}
					genTextClassName={'medium1'}
					btn1Text={'No, Cancel'}
					btn1OnClick={handleCloseConfirmation}
					no2btn={false}
					btn1BorderColor={'#fff'}
					btn1Color={'#0A0909'}
					btn2Text={'Proceed'}
				>
					<p className='medium5' style={{ marginTop: '20px' }}>
						Downloading this Stamp Paper will permanently mark its status as{' '}
						<b>Used</b>, and you will no longer be able to download or modify
						its status.
					</p>
				</GeneralModal>
				<Grid>
					<Box
						sx={{ marginBottom: '8px', marginTop: isMobile ? '8px' : '0px' }}
					>
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							{!inventoryPage && (
								<div>
									{docName.split(' ').length > 1
										? docName
										: docName.slice(0, Math.max(0, (isMobile && 20) || 30))
											|| '-'}
								</div>
							)}
							<Box
								onClick={() => {
									setDocumentLoading(true);
									handleClose();
								}}
							>
								<CrossIcon
									style={{
										stroke: 'white',
										cursor: 'pointer',
										width: '24px',
										height: '24px'
									}}
								/>
							</Box>
						</div>
					</Box>
					<Box display='flex' gap='20px'>
						<div
							style={{
								scrollbarWidth: 'none',
								maxHeight: '72vh',
								maxWidth: '600px'
							}}
							className='popUp-pdf-content custom-scrollbar'
						>
							{!inventoryPage && isPdf && documentLoading && (
								<Box
									sx={{
										minWidth: '600px',
										minHeight: '75vh',
										maxWidth: '600px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center'
									}}
								>
									<div style={{ textAlign: 'center' }}>
										<CircularProgress />
										<p>loading document...</p>
									</div>
								</Box>
							)}

							{!inventoryPage && (
								<div
									ref={pdfContainerRef}
									style={{
										width: '100%',
										display: 'flex',
										position: 'relative',
										overflow: 'auto',
										justifyContent: 'center',
										visibility: isPdf && documentLoading ? 'hidden' : 'visible'
									}}
									onScroll={handleScroll}
								>
									{isPdf ? (
										<Document
											style={{ width: '100%', height: '70vh' }}
											file={DocUrl}
											onLoadSuccess={onDocumentLoadSuccess}
											onLoadError={onDocumentLoadError}
										>
											{Array.from({ length: numPages }, (_, index) => (
												<div key={index}>
													<Page
														pageNumber={index + 1}
														renderTextLayer={false}
														renderAnnotationLayer={false}
														onRenderSuccess={() => {
															if (index === numPages - 1) {
																setDocumentLoading(false);
															}
														}}
														onRenderError={() => {
															setDocumentLoading(false);
														}}
														_className='pdf-pop-up-container'
													/>
													{index < numPages - 1 && (
														<hr
															style={{
																borderTop: '1px solid #000',
																margin: '20px 0'
															}}
														/>
													)}
												</div>
											))}
										</Document>
									) : (
										<div
											style={{
												minWidth: '500px',
												maxWidth: '600px',
												width: '100%'
											}}
										>
											<Box
												component={'img'}
												style={{
													minHeight: 'auto',
													objectFit: 'contain',
													width: '100%',
													margin: 'auto'
												}}
												src={DocUrl}
												alt={'image'}
											/>
										</div>
									)}
								</div>
							)}
						</div>
						{inventoryPage && (
							<Box position='relative' pr='10px'>
								<Box
									display='flex'
									justifyContent='space-between'
									alignItems='center'
								>
									<Typography variant='h6' className={'medium11'}>
										Details
									</Typography>
									{noDownloadIcon.includes(data.status) && (
										<Box
											component={'img'}
											src={DownloadIcon}
											alt='Download Icon'
											style={{
												cursor: 'pointer',
												width: '50px',
												height: '40px',
												marginRight: '25px'
											}}
											onClick={() => {
												handleOpenConfirmation();
											}}
										/>
									)}
								</Box>

								<CustomText title='State ID' value={data.stateId} />
								<CustomText title='State Name' value={data.stateName} />
								<CustomText
									title='Stamp Certificate ID'
									value={data.stampCertificateId}
								/>
								<CustomText title='Status' value={data.status} />
								<CustomText
									title='Article Code'
									value={data?.articleCode || '-'}
								/>

								{data.remark.length > 0 && (
									<Box mt='20px'>
										<Typography variant='h6' className={'medium11'}>
											History
										</Typography>
										<Box
											sx={{
												maxHeight: (332 * data.remark.length) + 'px',
												overflowY: 'auto'
											}}
										>
											{data.remark.map((item, idx) => (
												<Box
													key={'inventory' + idx}
													sx={{
														border: '1px solid white',
														borderRadius: '10px',
														p: '10px 15px',
														mt: '10px'
													}}
												>
													<CustomText
														title='Transition From'
														value={item.transitionFrom}
													/>
													<CustomText title='Remarks' value={item.remark} />
													<CustomText
														title='Updated By'
														value={item.updateBy}
													/>
													<CustomText title='Timestamp' value={item.updateAt} />
												</Box>
											))}
										</Box>
									</Box>
								)}
							</Box>
						)}
					</Box>
					{!inventoryPage && !documentLoading && isPdf && (
						<div>
							<p>
								{currentPage}/{numPages}
							</p>
						</div>
					)}
				</Grid>
			</Box>
		</Modal>
	);
};

export default ViewDocumentPopUp;

CustomText.propTypes = {
	title: PropTypes.any,
	value: PropTypes.any
};

ViewDocumentPopUp.propTypes = {
	open: PropTypes.any,
	docName: PropTypes.any,
	DocUrl: PropTypes.any,
	isPdf: PropTypes.any,
	handleClose: PropTypes.any,
	data: PropTypes.any,
	inventoryPage: PropTypes.any,
	handleUpdateClick: PropTypes.any,
	handleCloseConfirmation: PropTypes.any,
	handleOpenConfirmation: PropTypes.any,
	openConfirmation: PropTypes.any
};
